import { useCallback, useState } from "react";

function getQueryParam<T extends string | number | boolean>(key: string, defaultValue: T) {
  const parsedUrl = new URL(window.location.href);
  const params = new URLSearchParams(parsedUrl.searchParams);
  const value = params.get(key);

  if (!value) {
    return defaultValue;
  }

  if (typeof defaultValue === "number") {
    return parseInt(value, 10) as T;
  }
  if (typeof defaultValue === "boolean") {
    return (value === "true") as T;
  }
  return value as T;
}

type Response<T> = [T, (newValue: T) => void];

export function useQueryParam<T extends string | number | boolean>(
  key: string,
  defaultValue: T,
  persist?: boolean
): Response<T> {
  const [value, setValue] = useState<T>(getQueryParam(key, defaultValue));

  const customSetValue = useCallback(
    (newValue: T) => {
      if (persist) {
        const parsedUrl = new URL(window.location.href);
        const params = new URLSearchParams(parsedUrl.searchParams);
        params.set(key, newValue.toString());
        const modifiedUrl = `${parsedUrl.origin}${parsedUrl.pathname}${
          params.toString() ? `?${params.toString()}` : ""
        }`;
        window.history.replaceState("", "", modifiedUrl);
      }

      setValue(newValue);
    },
    [key, persist]
  );

  return [value, customSetValue];
}
